/**
 * Aulæ Wishlist — localStorage implementation
 * Stores product handles in localStorage under 'aulae_wishlist'.
 * Dispatches 'wishlist:change' on window after every update.
 */

const STORAGE_KEY = 'aulae_wishlist';

const WishlistManager = {
  get() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch { return []; }
  },
  set(handles) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    window.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles } }));
  },
  has(handle) { return this.get().includes(handle); },
  add(handle) {
    const list = this.get();
    if (!list.includes(handle)) this.set([...list, handle]);
  },
  remove(handle) { this.set(this.get().filter(h => h !== handle)); },
  toggle(handle) { this.has(handle) ? this.remove(handle) : this.add(handle); },
  count() { return this.get().length; },
};

// ─── Wishlist button component ────────────────────────────────────────────────
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    this.btn = this.querySelector('button');
    this._sync();
    this.btn.addEventListener('click', () => {
      WishlistManager.toggle(this.handle);
    });
    window.addEventListener('wishlist:change', () => this._sync());
  }

  _sync() {
    const active = WishlistManager.has(this.handle);
    this.btn.classList.toggle('is-active', active);
    this.btn.setAttribute('aria-pressed', active);
    this.btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
  }
}

customElements.define('wishlist-button', WishlistButton);

// ─── Header count badge ───────────────────────────────────────────────────────
function updateWishlistCount() {
  document.querySelectorAll('[data-wishlist-count]').forEach(el => {
    const count = WishlistManager.count();
    el.textContent = count;
    el.closest('[data-wishlist-link]')?.classList.toggle('has-items', count > 0);
  });
}

window.addEventListener('wishlist:change', updateWishlistCount);
document.addEventListener('DOMContentLoaded', updateWishlistCount);

// ─── Wishlist page renderer ───────────────────────────────────────────────────
class WishlistPage extends HTMLElement {
  connectedCallback() {
    this._render();
    window.addEventListener('wishlist:change', () => this._render());
  }

  async _render() {
    const handles = WishlistManager.get();
    const empty = this.querySelector('[data-wishlist-empty]');
    const grid = this.querySelector('[data-wishlist-grid]');

    if (handles.length === 0) {
      if (empty) empty.hidden = false;
      if (grid) grid.innerHTML = '';
      return;
    }

    if (empty) empty.hidden = true;

    // Fetch product cards via Section Rendering API
    const cards = await Promise.all(handles.map(h => this._fetchCard(h)));
    if (grid) grid.innerHTML = cards.filter(Boolean).join('');
  }

  async _fetchCard(handle) {
    try {
      const res = await fetch(`/products/${handle}?section_id=wishlist-card`);
      if (!res.ok) return null;
      return await res.text();
    } catch { return null; }
  }
}

customElements.define('wishlist-page', WishlistPage);

window.WishlistManager = WishlistManager;
